// KYC constants for the "Podatki stranke" page and profile.
//
// `value` fields are persisted to the database and must never change.
// `labelKey` fields are translation keys resolved via useLanguage().t() at
// the render site — this module itself does not call t().

export const LEGAL_FORMS = [
  { value: 'd.o.o.', labelKey: 'kyc.legalForm.doo' },
  { value: 's.p.', labelKey: 'kyc.legalForm.sp' },
  { value: 'd.d.', labelKey: 'kyc.legalForm.dd' },
  { value: 'd.n.o.', labelKey: 'kyc.legalForm.dno' },
  { value: 'k.d.', labelKey: 'kyc.legalForm.kd' },
  { value: 'zavod', labelKey: 'kyc.legalForm.zavod' },
  { value: 'drustvo', labelKey: 'kyc.legalForm.drustvo' }
];

export const ID_DOC_TYPES = [
  { value: 'osebna', labelKey: 'kyc.idDocType.osebna' },
  { value: 'potni_list', labelKey: 'kyc.idDocType.potniList' }
];

export const PURPOSES = [
  { value: 'nalozba', labelKey: 'kyc.purpose.nalozba' },
  { value: 'darilo', labelKey: 'kyc.purpose.darilo' },
  { value: 'zbirateljstvo', labelKey: 'kyc.purpose.zbirateljstvo' },
  { value: 'preprodaja', labelKey: 'kyc.purpose.preprodaja' },
  { value: 'zascita_premozenja', labelKey: 'kyc.purpose.zascitaPremozenja' },
  { value: 'ostalo', labelKey: 'kyc.purpose.ostalo' }
];

export const PAYMENT_METHODS = [
  { value: 'banka', labelKey: 'kyc.paymentMethod.banka' },
  { value: 'kripto', labelKey: 'kyc.paymentMethod.kripto' },
  { value: 'gotovina', labelKey: 'kyc.paymentMethod.gotovina' },
  { value: 'kombinacija', labelKey: 'kyc.paymentMethod.kombinacija' }
];

export const SOURCES_OF_FUNDS = [
  { value: 'prihranki', labelKey: 'kyc.sourceOfFunds.prihranki' },
  { value: 'placa', labelKey: 'kyc.sourceOfFunds.placa' },
  { value: 'pokojnina', labelKey: 'kyc.sourceOfFunds.pokojnina' },
  { value: 'studentsko_delo', labelKey: 'kyc.sourceOfFunds.studentskoDelo' },
  { value: 'honorar', labelKey: 'kyc.sourceOfFunds.honorar' },
  { value: 'stipendija', labelKey: 'kyc.sourceOfFunds.stipendija' },
  { value: 'regres', labelKey: 'kyc.sourceOfFunds.regres' },
  { value: 'posel', labelKey: 'kyc.sourceOfFunds.posel' },
  { value: 'prodaja_nepremicnine', labelKey: 'kyc.sourceOfFunds.prodajaNepremicnine' },
  { value: 'oddaja_nepremicnine', labelKey: 'kyc.sourceOfFunds.oddajaNepremicnine' },
  { value: 'prodaja_vozila', labelKey: 'kyc.sourceOfFunds.prodajaVozila' },
  { value: 'vrednostni_papirji', labelKey: 'kyc.sourceOfFunds.vrednostniPapirji' },
  { value: 'kripto', labelKey: 'kyc.sourceOfFunds.kripto' },
  { value: 'varcevanje_zavarovanje', labelKey: 'kyc.sourceOfFunds.varcevanjeZavarovanje' },
  { value: 'prodaja_zlata_kovin', labelKey: 'kyc.sourceOfFunds.prodajaZlataKovin' },
  { value: 'dedovanje', labelKey: 'kyc.sourceOfFunds.dedovanje' },
  { value: 'ostalo', labelKey: 'kyc.sourceOfFunds.ostalo' }
];

export const HOW_HEARD = [
  { value: 'splet', labelKey: 'kyc.howHeard.splet' },
  { value: 'socialna_omrezja', labelKey: 'kyc.howHeard.socialnaOmrezja' },
  { value: 'priporocilo', labelKey: 'kyc.howHeard.priporocilo' },
  { value: 'oglas', labelKey: 'kyc.howHeard.oglas' },
  { value: 'mediji', labelKey: 'kyc.howHeard.mediji' },
  { value: 'ostalo', labelKey: 'kyc.howHeard.ostalo' }
];

// Six of the eight entries below are registered legal entity names (proper
// nouns) where `label` already equals `value` — they stay literal in both
// languages. Only the placeholder and "other bank" entries are real UI
// strings, so only those two get a `labelKey`. Render sites must handle
// both: `b.labelKey ? t(b.labelKey) : b.label`.
export const BANKS = [
  { value: '', labelKey: 'kyc.bank.placeholder' },
  { value: 'Nova Ljubljanska banka d.d.', label: 'Nova Ljubljanska banka d.d.' },
  { value: 'OTP banka Slovenija d.d.', label: 'OTP banka Slovenija d.d.' },
  { value: 'Intesa Sanpaolo Bank d.d.', label: 'Intesa Sanpaolo Bank d.d.' },
  { value: 'Delavska hranilnica d.d.', label: 'Delavska hranilnica d.d.' },
  { value: 'UniCredit Banka Slovenija d.d.', label: 'UniCredit Banka Slovenija d.d.' },
  { value: 'Gorenjska banka d.d.', label: 'Gorenjska banka d.d.' },
  { value: 'other', labelKey: 'kyc.bank.other' }
];

// Common countries (Slovenia first). Format/checksum validation only for now.
//
// `value` is the string persisted to the database and MUST stay byte-for-byte
// identical to the original plain-string array (including 'Slovenia', which
// was already English in the source) — existing customer records store
// these exact strings.
export const COUNTRIES = [
  { value: 'Slovenia', labelKey: 'kyc.country.slovenia' },
  { value: 'Avstrija', labelKey: 'kyc.country.austria' },
  { value: 'Belgija', labelKey: 'kyc.country.belgium' },
  { value: 'Bolgarija', labelKey: 'kyc.country.bulgaria' },
  { value: 'Bosna in Hercegovina', labelKey: 'kyc.country.bosniaAndHerzegovina' },
  { value: 'Ciper', labelKey: 'kyc.country.cyprus' },
  { value: 'Češka', labelKey: 'kyc.country.czechia' },
  { value: 'Črna gora', labelKey: 'kyc.country.montenegro' },
  { value: 'Danska', labelKey: 'kyc.country.denmark' },
  { value: 'Estonija', labelKey: 'kyc.country.estonia' },
  { value: 'Finska', labelKey: 'kyc.country.finland' },
  { value: 'Francija', labelKey: 'kyc.country.france' },
  { value: 'Grčija', labelKey: 'kyc.country.greece' },
  { value: 'Hrvaška', labelKey: 'kyc.country.croatia' },
  { value: 'Irska', labelKey: 'kyc.country.ireland' },
  { value: 'Islandija', labelKey: 'kyc.country.iceland' },
  { value: 'Italija', labelKey: 'kyc.country.italy' },
  { value: 'Latvija', labelKey: 'kyc.country.latvia' },
  { value: 'Lihtenštajn', labelKey: 'kyc.country.liechtenstein' },
  { value: 'Litva', labelKey: 'kyc.country.lithuania' },
  { value: 'Luksemburg', labelKey: 'kyc.country.luxembourg' },
  { value: 'Madžarska', labelKey: 'kyc.country.hungary' },
  { value: 'Malta', labelKey: 'kyc.country.malta' },
  { value: 'Nemčija', labelKey: 'kyc.country.germany' },
  { value: 'Nizozemska', labelKey: 'kyc.country.netherlands' },
  { value: 'Norveška', labelKey: 'kyc.country.norway' },
  { value: 'Poljska', labelKey: 'kyc.country.poland' },
  { value: 'Portugalska', labelKey: 'kyc.country.portugal' },
  { value: 'Romunija', labelKey: 'kyc.country.romania' },
  { value: 'Severna Makedonija', labelKey: 'kyc.country.northMacedonia' },
  { value: 'Slovaška', labelKey: 'kyc.country.slovakia' },
  { value: 'Srbija', labelKey: 'kyc.country.serbia' },
  { value: 'Španija', labelKey: 'kyc.country.spain' },
  { value: 'Švedska', labelKey: 'kyc.country.sweden' },
  { value: 'Švica', labelKey: 'kyc.country.switzerland' },
  { value: 'Velika Britanija', labelKey: 'kyc.country.unitedKingdom' },
  // Non-European markets
  { value: 'Združene države Amerike', labelKey: 'kyc.country.unitedStates' },
  { value: 'Japonska', labelKey: 'kyc.country.japan' },
  { value: 'Združeni arabski emirati', labelKey: 'kyc.country.uae' },
  { value: 'Turčija', labelKey: 'kyc.country.turkey' },
  { value: 'Južna Afrika', labelKey: 'kyc.country.southAfrica' },
  { value: 'Brazilija', labelKey: 'kyc.country.brazil' },
  { value: 'Rusija', labelKey: 'kyc.country.russia' },
  { value: 'Ukrajina', labelKey: 'kyc.country.ukraine' },
  { value: 'Drugo', labelKey: 'kyc.country.other' }
];

// Official Slovenian sanctions / restrictive measures information.
export const SANCTIONS_LINK =
  'https://www.gov.si/teme/omejevalni-ukrepi/';
